import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, XIcon, CheckIcon } from 'lucide-react';
const ANNUAL_URL = 'https://go.peptidescienceinstitute.com/pm-pro-annual-fm';
const MONTHLY_URL = 'https://go.peptidescienceinstitute.com/pm-pro-monthly-fm';
export function MonthlyUpsellModal({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return <AnimatePresence data-id="element-1400">
      {isOpen && <motion.div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-1401">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-black/80 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-1402" />

          {/* Modal */}
          <motion.div className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-xl border border-amber-600/40 bg-gradient-to-b from-stone-950 via-black to-stone-950 shadow-[0_0_80px_rgba(217,119,6,0.15),0_0_200px_rgba(217,119,6,0.05)]" initial={{
        opacity: 0,
        scale: 0.9,
        y: 40
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.35,
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} onClick={e => e.stopPropagation()} data-id="element-1403">
            {/* Close button */}
            <button onClick={onClose} className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-stone-800/80 hover:bg-stone-700 text-stone-400 hover:text-white transition-colors" aria-label="Close" data-id="element-1404">
              <XIcon className="w-4 h-4" data-id="element-1405" />
            </button>

            <div className="px-6 sm:px-8 py-8 sm:py-10" data-id="element-1406">
              {/* Icon */}
              <div className="flex justify-center mb-5" data-id="element-1407">
                <div className="relative" data-id="element-1408">
                  <div className="absolute inset-0 bg-amber-500/20 blur-xl rounded-full scale-[2.5]" data-id="element-1409" />
                  <div className="relative w-10 h-10 flex items-center justify-center" data-id="element-1410">
                    <SparklesIcon className="w-7 h-7 text-amber-400" data-id="element-1411" />
                  </div>
                </div>
              </div>

              {/* Headline */}
              <h2 className="text-2xl sm:text-3xl font-bold text-center leading-tight mb-4" style={{
            fontFamily: "'Cinzel', serif",
            color: '#fbbf24',
            textShadow: '0 0 30px rgba(251, 191, 36, 0.25)'
          }} data-id="element-1412">
                You're About to Take a Powerful Step...
              </h2>

              {/* Validation copy */}
              <p className="text-center text-stone-300 text-sm sm:text-base leading-relaxed mb-6" style={{
            fontFamily: "'Inter', system-ui, sans-serif"
          }} data-id="element-1413">
                Joining PSI PRO puts you in the{' '}
                <span className="text-amber-400 font-semibold" data-id="element-1414">top 1%</span> of
                people who refuse to accept the limits of conventional medicine.
                <br data-id="element-1415" />
                <span className="text-stone-400 mt-1 inline-block" data-id="element-1416">
                  Before you lock in your membership...
                </span>
              </p>

              {/* Reframe highlight box */}
              <div className="relative rounded-lg p-4 sm:p-5 mb-6 border border-amber-500/40 bg-amber-950/25" style={{
            boxShadow: '0 0 30px rgba(217, 119, 6, 0.1), inset 0 0 30px rgba(217, 119, 6, 0.05)'
          }} data-id="element-1417">
                <p className="text-center text-lg sm:text-xl font-bold text-amber-300 leading-snug" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-1418">
                  Did you know you could save{' '}
                  <span className="text-green-400" style={{
                textShadow: '0 0 15px rgba(74, 222, 128, 0.3)'
              }} data-id="element-1419">
                    $498
                  </span>{' '}
                  — and unlock everything{' '}
                  <span className="text-cyan-300 italic" data-id="element-1420">instantly</span>?
                </p>
              </div>

              {/* Comparison */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8 pt-4" data-id="element-1421">
                {/* Monthly column */}
                <div className="rounded-lg border border-stone-700/60 bg-stone-900/50 p-4 sm:p-5" data-id="element-1422">
                  <p className="text-xs uppercase tracking-[0.15em] text-stone-500 mb-2" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1423">
                    Monthly Plan
                  </p>
                  <p className="text-white font-bold text-lg mb-3" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1424">
                    $249
                    <span className="text-stone-400 font-normal text-sm" data-id="element-1425">
                      /mo × 12
                    </span>
                  </p>
                  <div className="space-y-2 text-sm text-stone-400" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1426">
                    <div className="flex items-start gap-2" data-id="element-1427">
                      <span className="text-stone-600 mt-0.5" data-id="element-1428">—</span>
                      <span data-id="element-1429">$2,988 total cost</span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1430">
                      <span className="text-stone-600 mt-0.5" data-id="element-1431">—</span>
                      <span data-id="element-1432">Course unlocked monthly over 12 months</span>
                    </div>
                  </div>
                </div>

                {/* Annual column */}
                <div className="relative rounded-lg border-2 border-green-500/50 bg-gradient-to-b from-green-950/30 to-black p-4 sm:p-5 pt-6 sm:pt-7" style={{
              boxShadow: '0 0 25px rgba(34, 197, 94, 0.1)'
            }} data-id="element-1433">
                  {/* Most Popular badge */}
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10" data-id="element-1434">
                    <span className="inline-block whitespace-nowrap px-4 py-1 bg-green-500 text-black text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded-full shadow-[0_0_12px_rgba(34,197,94,0.4)]" style={{
                  fontFamily: "'Inter', system-ui, sans-serif"
                }} data-id="element-1435">
                      Most Popular
                    </span>
                  </div>
                  <p className="text-xs uppercase tracking-[0.15em] text-green-400/70 mb-2 mt-1" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1436">
                    Annual — Pay in Full
                  </p>
                  <p className="text-white font-bold text-lg mb-3" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1437">
                    $2,490{' '}
                    <span className="text-green-400 font-semibold text-sm" data-id="element-1438">
                      save $498
                    </span>
                  </p>
                  <div className="space-y-2 text-sm" style={{
                fontFamily: "'Inter', system-ui, sans-serif"
              }} data-id="element-1439">
                    <div className="flex items-start gap-2" data-id="element-1440">
                      <CheckIcon className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" data-id="element-1441" />
                      <span className="text-green-300 font-medium" data-id="element-1442">
                        FULL Peptide Mastery 2.0 unlocked IMMEDIATELY
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1443">
                      <CheckIcon className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" data-id="element-1444" />
                      <span className="text-stone-300" data-id="element-1445">
                        Grandfathered rate locked forever
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1446">
                      <CheckIcon className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" data-id="element-1447" />
                      <span className="text-stone-300" data-id="element-1448">
                        One payment, zero friction
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Primary CTA — Annual */}
              <a href={ANNUAL_URL} className="block w-full px-6 py-4 sm:py-5 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white text-center font-bold text-base sm:text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(34,197,94,0.4)] hover:shadow-[0_0_60px_rgba(34,197,94,0.6)] hover:scale-[1.02] min-h-[44px]" style={{
            fontFamily: "'Inter', system-ui, sans-serif"
          }} data-id="element-1449">
                Yes — Unlock Everything Now for $2,490
              </a>

              {/* Secondary CTA — Monthly (no guilt) */}
              <a href={MONTHLY_URL} className="block w-full mt-3 px-6 py-3 sm:py-4 text-center text-stone-400 hover:text-stone-200 text-sm sm:text-base font-medium rounded-lg border border-stone-700/50 hover:border-stone-600 bg-transparent hover:bg-stone-900/50 transition-all duration-300 min-h-[44px]" style={{
            fontFamily: "'Inter', system-ui, sans-serif"
          }} data-id="element-1450">
                Continue with Monthly Plan ($249/mo)
              </a>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}